/**
 * Gap Detail Modal Component
 * Displays detailed information about a department's data coverage
 */

import React from 'react';
import { X, Calendar, Database, RefreshCw, TrendingUp, Copy, Clock, AlertCircle } from 'lucide-react';

/**
 * Format currency value
 */
const formatCurrency = (value) => {
  if (value == null) return '$0';
  const num = parseFloat(value);
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
  return `$${num.toFixed(0)}`;
};

/**
 * Format number with commas
 */
const formatNumber = (value) => {
  if (value == null) return '0';
  return parseInt(value, 10).toLocaleString();
};

/**
 * Get freshness status color
 */
const getFreshnessClass = (status) => {
  switch (status) {
    case 'current': return 'dga-modal-status-current';
    case 'aging': return 'dga-modal-status-aging';
    case 'stale': return 'dga-modal-status-stale';
    case 'very_stale': return 'dga-modal-status-very-stale';
    case 'no_data': return 'dga-modal-status-no-data';
    default: return '';
  }
};

/**
 * Gap Detail Modal Component
 */
const GapDetailModal = ({ department, onClose, onNavigateToScraper }) => {
  if (!department) return null;

  const {
    department_id,
    official_name,
    common_name,
    acronym,
    department_code,
    earliest_data,
    latest_data,
    total_records,
    duplicate_count,
    total_spend,
    months_with_data,
    coverage_percentage,
    days_since_last_data,
    freshness_status,
    last_import_date
  } = department;

  const handleScrape = () => {
    onNavigateToScraper?.({ department_id });
    onClose?.();
  };

  return (
    <div className="dga-modal-overlay" onClick={onClose}>
      <div className="dga-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="dga-modal-header">
          <div className="dga-modal-title">
            <span className="dga-modal-acronym">{acronym || department_code || '—'}</span>
            <div>
              <h2>{common_name || official_name}</h2>
              {common_name && official_name && common_name !== official_name && (
                <p className="dga-modal-official">{official_name}</p>
              )}
            </div>
          </div>
          <button className="dga-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Status Badge */}
        <div className="dga-modal-status-row">
          <span className={`dga-modal-status-badge ${getFreshnessClass(freshness_status)}`}>
            {freshness_status?.replace('_', ' ') || 'unknown'}
          </span>
          {days_since_last_data != null && (
            <span className="dga-modal-days">
              <Clock size={14} />
              {days_since_last_data} days since last data
            </span>
          )}
        </div>

        {/* Stats Grid */}
        <div className="dga-modal-stats">
          <div className="dga-modal-stat">
            <Database size={18} />
            <div className="dga-modal-stat-content">
              <span className="dga-modal-stat-value">{formatNumber(total_records)}</span>
              <span className="dga-modal-stat-label">Total Records</span>
            </div>
          </div>

          <div className="dga-modal-stat">
            <TrendingUp size={18} />
            <div className="dga-modal-stat-content">
              <span className="dga-modal-stat-value">{formatCurrency(total_spend)}</span>
              <span className="dga-modal-stat-label">Total Spend</span>
            </div>
          </div>

          <div className="dga-modal-stat">
            <Calendar size={18} />
            <div className="dga-modal-stat-content">
              <span className="dga-modal-stat-value">{months_with_data || 0}</span>
              <span className="dga-modal-stat-label">Months with Data</span>
            </div>
          </div>

          <div className="dga-modal-stat">
            <Copy size={18} />
            <div className="dga-modal-stat-content">
              <span className="dga-modal-stat-value">
                {formatNumber(duplicate_count)} ({((duplicate_count / total_records) * 100 || 0).toFixed(1)}%)
              </span>
              <span className="dga-modal-stat-label">Duplicates</span>
            </div>
          </div>
        </div>

        {/* Coverage Info */}
        <div className="dga-modal-section">
          <h3>Coverage Details</h3>
          <div className="dga-modal-coverage">
            <div className="dga-modal-coverage-bar-wrapper">
              <div className="dga-modal-coverage-bar-bg">
                <div
                  className="dga-modal-coverage-bar"
                  style={{ width: `${Math.min(coverage_percentage || 0, 100)}%` }}
                />
              </div>
              <span className="dga-modal-coverage-pct">{coverage_percentage || 0}% coverage</span>
            </div>
            <div className="dga-modal-date-range">
              <div className="dga-modal-date">
                <span className="dga-modal-date-label">Earliest Data</span>
                <span className="dga-modal-date-value">
                  {earliest_data ? new Date(earliest_data).toLocaleDateString() : 'No data'}
                </span>
              </div>
              <div className="dga-modal-date-arrow">→</div>
              <div className="dga-modal-date">
                <span className="dga-modal-date-label">Latest Data</span>
                <span className="dga-modal-date-value">
                  {latest_data ? new Date(latest_data).toLocaleDateString() : 'No data'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Last Import */}
        {last_import_date && (
          <div className="dga-modal-section">
            <h3>Last Import</h3>
            <p className="dga-modal-import-date">
              {new Date(last_import_date).toLocaleDateString()} at{' '}
              {new Date(last_import_date).toLocaleTimeString()}
            </p>
          </div>
        )}

        {/* Warning for stale data */}
        {(freshness_status === 'stale' || freshness_status === 'very_stale' || freshness_status === 'no_data') && (
          <div className="dga-modal-warning">
            <AlertCircle size={16} />
            <span>
              {freshness_status === 'no_data'
                ? 'No procurement data available for this department. Consider initiating a scrape.'
                : `Data is ${freshness_status.replace('_', ' ')} (${days_since_last_data} days old). Consider refreshing.`
              }
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="dga-modal-actions">
          <button className="dga-btn dga-btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="dga-btn dga-btn-primary" onClick={handleScrape}>
            <RefreshCw size={14} />
            Scrape Department
          </button>
        </div>
      </div>

      <style>{`
        .dga-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .dga-modal {
          background: var(--color-bg-primary, #0a0a0a);
          border: 1px solid var(--color-border, #333);
          border-radius: 16px;
          width: 100%;
          max-width: 560px;
          max-height: 90vh;
          overflow-y: auto;
          animation: dga-modal-appear 0.2s ease;
        }

        @keyframes dga-modal-appear {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .dga-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 24px 24px 16px;
          border-bottom: 1px solid var(--color-border, #333);
        }

        .dga-modal-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .dga-modal-acronym {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--color-primary, #6366f1);
          background: rgba(99, 102, 241, 0.1);
          padding: 8px 12px;
          border-radius: 8px;
        }

        .dga-modal-title h2 {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--color-text-primary, #fff);
          margin: 0;
        }

        .dga-modal-official {
          font-size: 0.8125rem;
          color: var(--color-text-tertiary, #666);
          margin: 4px 0 0;
        }

        .dga-modal-close {
          background: transparent;
          border: none;
          color: var(--color-text-secondary, #888);
          cursor: pointer;
          padding: 8px;
          border-radius: 8px;
          transition: all 0.15s;
        }

        .dga-modal-close:hover {
          background: var(--color-bg-hover, #252525);
          color: var(--color-text-primary, #fff);
        }

        .dga-modal-status-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 24px;
          background: var(--color-bg-secondary, #1a1a1a);
        }

        .dga-modal-status-badge {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          padding: 6px 12px;
          border-radius: 16px;
        }

        .dga-modal-status-badge.dga-modal-status-current {
          background: rgba(16, 185, 129, 0.15);
          color: var(--color-success, #10b981);
        }

        .dga-modal-status-badge.dga-modal-status-aging {
          background: rgba(245, 158, 11, 0.15);
          color: var(--color-warning, #f59e0b);
        }

        .dga-modal-status-badge.dga-modal-status-stale {
          background: rgba(249, 115, 22, 0.15);
          color: var(--color-orange, #f97316);
        }

        .dga-modal-status-badge.dga-modal-status-very-stale {
          background: rgba(239, 68, 68, 0.15);
          color: var(--color-error, #ef4444);
        }

        .dga-modal-status-badge.dga-modal-status-no-data {
          background: rgba(102, 102, 102, 0.15);
          color: var(--color-text-tertiary, #666);
        }

        .dga-modal-days {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.8125rem;
          color: var(--color-text-secondary, #888);
        }

        .dga-modal-stats {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          padding: 20px 24px;
        }

        .dga-modal-stat {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: var(--color-bg-secondary, #1a1a1a);
          border-radius: 12px;
        }

        .dga-modal-stat svg {
          color: var(--color-primary, #6366f1);
          flex-shrink: 0;
        }

        .dga-modal-stat-content {
          display: flex;
          flex-direction: column;
        }

        .dga-modal-stat-value {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--color-text-primary, #fff);
        }

        .dga-modal-stat-label {
          font-size: 0.6875rem;
          color: var(--color-text-tertiary, #666);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .dga-modal-section {
          padding: 0 24px 20px;
        }

        .dga-modal-section h3 {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-text-secondary, #888);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin: 0 0 12px;
        }

        .dga-modal-coverage {
          background: var(--color-bg-secondary, #1a1a1a);
          border-radius: 12px;
          padding: 16px;
        }

        .dga-modal-coverage-bar-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .dga-modal-coverage-bar-bg {
          flex: 1;
          height: 8px;
          background: var(--color-bg-tertiary, #252525);
          border-radius: 4px;
          overflow: hidden;
        }

        .dga-modal-coverage-bar {
          height: 100%;
          background: var(--color-primary, #6366f1);
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .dga-modal-coverage-pct {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--color-text-primary, #fff);
          white-space: nowrap;
        }

        .dga-modal-date-range {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .dga-modal-date {
          text-align: center;
        }

        .dga-modal-date-label {
          display: block;
          font-size: 0.6875rem;
          color: var(--color-text-tertiary, #666);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }

        .dga-modal-date-value {
          font-size: 0.875rem;
          color: var(--color-text-primary, #fff);
        }

        .dga-modal-date-arrow {
          font-size: 1.25rem;
          color: var(--color-text-tertiary, #666);
        }

        .dga-modal-import-date {
          font-size: 0.875rem;
          color: var(--color-text-primary, #fff);
          margin: 0;
        }

        .dga-modal-warning {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin: 0 24px 20px;
          padding: 12px 16px;
          background: rgba(239, 68, 68, 0.1);
          border-radius: 8px;
          font-size: 0.8125rem;
          color: var(--color-error, #ef4444);
        }

        .dga-modal-warning svg {
          flex-shrink: 0;
          margin-top: 2px;
        }

        .dga-modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid var(--color-border, #333);
          background: var(--color-bg-secondary, #1a1a1a);
        }

        @media (max-width: 480px) {
          .dga-modal-stats {
            grid-template-columns: 1fr;
          }

          .dga-modal-actions {
            flex-direction: column;
          }

          .dga-modal-actions .dga-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default GapDetailModal;
